import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Box, Tab, Tabs, Typography } from '@mui/material';
import MainLayout from '../../components/layout/MainLayout';
import { useAuth } from '../../contexts/AuthContext';
import { getTenants, getTenantSupport, getTwilioBillingTenants } from '../../api/backendApi';
import { currentUtcMonth, formatVendorCosts } from './billingFormat';
import TenantDetailPage from './TenantDetailPage';
import BillingPage from './BillingPage';
import {
  ALL_TENANT_SUPPORT_CAP,
  ALL_TENANTS,
  normalizeSupportTab,
  parseSupportLocation,
  supportPath,
  supportSearch,
  tenantStatusLabel,
} from './supportNav';
import { canOpenSupportWorkspace, supportTabAllowed, visibleSupportTabs } from './supportWorkspaceAccess';

export function LegacyTenantDetailRedirect() {
  const { tenantId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const target = supportPath({
    tenantId,
    tab: normalizeSupportTab(params.get('tab') || 'overview'),
    month: params.get('month') || '',
  });
  useEffect(() => {
    navigate(target, { replace: true });
  }, [navigate, target]);
  return null;
}

const TAB_LABELS = {
  overview: 'Overview',
  support: 'Support',
  billing: 'Billing',
  'data-usage': 'Data Usage',
  configuration: 'Configuration',
};

function unwrapList(response) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.items)) return response.data.items;
  if (Array.isArray(response?.tenants)) return response.tenants;
  return [];
}

function unwrap(response) {
  if (response && response.success === true && response.data) return response.data;
  return response || {};
}

export default function SupportWorkspacePage() {
  const { userClaims, userData } = useAuth();
  const role = userClaims?.role || userData?.role;
  const access = { role, permissions: userData?.permissions };
  const allowed = canOpenSupportWorkspace(access);
  const tabs = visibleSupportTabs(access);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = parseSupportLocation(searchParams);
  const explicitTab = searchParams.get('tab');
  const requestedTab = explicitTab ? normalizeSupportTab(explicitTab) : '';
  const tabDenied = Boolean(explicitTab) && !supportTabAllowed(access, requestedTab);
  const activeTab = tabDenied
    ? requestedTab
    : (supportTabAllowed(access, location.tab) ? location.tab : (tabs[0] || 'overview'));
  const month = /^\d{4}-(0[1-9]|1[0-2])$/.test(location.month) ? location.month : currentUtcMonth();
  const [tenants, setTenants] = useState([]);
  const [billingRows, setBillingRows] = useState(null);
  const [supportByTenant, setSupportByTenant] = useState({});
  const [supportCoverage, setSupportCoverage] = useState('unavailable');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshedAt, setRefreshedAt] = useState('');
  const [refreshTick, setRefreshTick] = useState(0);

  function updateLocation(patch, mode = 'push') {
    const next = supportSearch({
      tenantId: patch.tenantId ?? location.tenantId,
      tab: patch.tab ?? location.tab,
      month: patch.month ?? month,
    });
    searchParams.forEach((value, key) => {
      if (!['tenantId', 'tab', 'month'].includes(key) && !next.has(key)) next.set(key, value);
    });
    navigate(`/admin/support?${next.toString()}`, { replace: mode === 'replace' });
  }

  useEffect(() => {
    if (!allowed || tabDenied) return undefined;
    if (!searchParams.get('tenantId') || !searchParams.get('tab') || !supportTabAllowed(access, location.tab)) {
      updateLocation({ tab: tabs[0] || 'overview' }, 'replace');
    }
    return undefined;
  }, [allowed, searchParams, tabDenied]);

  useEffect(() => {
    if (!allowed) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const showBilling = supportTabAllowed(access, 'billing') && (activeTab === 'billing' || activeTab === 'overview');
        const showSupport = supportTabAllowed(access, 'support') && ['overview', 'support', 'configuration'].includes(activeTab);
        const [tenantResp, billingResp] = await Promise.all([
          getTenants({ includeInactive: true }),
          showBilling ? getTwilioBillingTenants(month).catch(() => null) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        const rows = unwrapList(tenantResp)
          .map((row) => ({
            ...row,
            id: row.id || row.tenantId,
            name: row.name || row.id || row.tenantId,
            statusLabel: tenantStatusLabel(row),
          }))
          .filter((row) => row.id)
          .sort((a, b) => String(a.name).localeCompare(String(b.name)));
        setTenants(rows);
        const billingData = billingResp ? unwrap(billingResp) : null;
        setBillingRows(billingData ? (billingData.tenants || []) : null);
        setRefreshedAt(new Date().toISOString());
        if (!showSupport) {
          setSupportByTenant({});
          setSupportCoverage('unavailable');
        } else {
          const sample = rows.slice(0, ALL_TENANT_SUPPORT_CAP);
          const entries = await Promise.all(sample.map(async (row) => {
            try {
              const view = unwrap(await getTenantSupport(row.id, { limit: 5 }));
              return [row.id, view];
            } catch (err) {
              return [row.id, { error: err?.message || 'Support view unavailable', measurement: 'unavailable' }];
            }
          }));
          if (cancelled) return;
          setSupportByTenant(Object.fromEntries(entries));
          setSupportCoverage(rows.length > sample.length ? 'partial' : 'measured');
        }
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || 'Support workspace could not be loaded.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [allowed, month, refreshTick, activeTab, tabDenied]);

  if (!allowed) {
    return (
      <MainLayout>
        <Box sx={{ p: 3 }} data-testid="support-workspace-denied">
          <Typography variant="h5">Signal Bridge Support</Typography>
          <p className="mt-2 text-sm text-gray-600">You do not have permission to open Signal Bridge Support.</p>
        </Box>
      </MainLayout>
    );
  }

  const selected = location.tenantId !== ALL_TENANTS;
  const billingById = new Map((billingRows || []).map((row) => [row.tenantId, row]));

  return (
    <MainLayout scrollMain>
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }} data-testid="support-workspace">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 650, color: '#111827' }}>
              Signal Bridge Support
            </Typography>
            <p className="mt-1 text-sm text-gray-600">Last refreshed {refreshedAt ? refreshedAt.replace('T', ' ').slice(0, 19) + ' UTC' : '—'}</p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-sm text-gray-700">
              Tenant
              <select
                className="mt-1 block rounded border border-gray-300 bg-white px-2 py-2 text-sm"
                data-testid="tenant-selector"
                value={location.tenantId}
                onChange={(event) => updateLocation({ tenantId: event.target.value })}
              >
                <option value={ALL_TENANTS}>All Tenants</option>
                {location.tenantId !== ALL_TENANTS && !tenants.some((row) => row.id === location.tenantId) ? (
                  <option value={location.tenantId}>{location.tenantId}</option>
                ) : null}
                {tenants.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.name}{row.statusLabel === 'Deleted' ? ' (Deleted)' : ''} — {row.id}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" className="text-sm text-blue-700 underline" data-testid="workspace-refresh" onClick={() => setRefreshTick((n) => n + 1)}>
              Refresh
            </button>
          </div>
        </div>

        <Tabs
          value={tabs.includes(activeTab) && !tabDenied ? activeTab : false}
          onChange={(_event, value) => updateLocation({ tab: value })}
          sx={{ mt: 2 }}
          data-testid="support-tabs"
        >
          {tabs.map((value) => <Tab key={value} value={value} label={TAB_LABELS[value]} />)}
        </Tabs>

        {tabDenied ? (
          <Alert severity="warning" sx={{ mt: 2 }} data-testid="support-tab-denied">
            You do not have permission to view {TAB_LABELS[requestedTab] || 'this tab'}.
          </Alert>
        ) : null}

        {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
        {loading ? <p className="mt-4 text-gray-600" data-testid="workspace-loading">Loading support workspace…</p> : null}

        {!loading && !tabDenied && !selected && activeTab === 'overview' ? (
          <AllTenantTable
            tenants={tenants}
            billingById={billingById}
            billingMeasured={billingRows !== null}
            supportByTenant={supportByTenant}
            supportCoverage={supportCoverage}
            showSupport={supportTabAllowed(access, 'support')}
            showBilling={supportTabAllowed(access, 'billing')}
            onOpen={(id, tab) => updateLocation({ tenantId: id, tab })}
          />
        ) : null}

        {!loading && !tabDenied && !selected && activeTab === 'support' ? (
          <AllTenantSupport
            tenants={tenants}
            supportByTenant={supportByTenant}
            supportCoverage={supportCoverage}
            onOpen={(id) => updateLocation({ tenantId: id, tab: 'support' })}
          />
        ) : null}

        {!loading && !tabDenied && !selected && activeTab === 'billing' ? (
          <div className="mt-4" data-testid="all-tenants-billing">
            <BillingPage embedded month={month} onMonthChange={(value) => updateLocation({ month: value })} />
          </div>
        ) : null}

        {!loading && !tabDenied && !selected && activeTab === 'data-usage' ? (
          <section className="mt-4" data-testid="all-tenants-usage">
            <p className="text-sm text-gray-600">Data usage is measured per tenant. Open a tenant for the measured inventory.</p>
            <TenantActionList tenants={tenants} actionLabel="Open Data Usage" onOpen={(id) => updateLocation({ tenantId: id, tab: 'data-usage' })} />
          </section>
        ) : null}

        {!loading && !tabDenied && !selected && activeTab === 'configuration' ? (
          <section className="mt-4" data-testid="all-tenants-configuration">
            <p className="text-sm text-gray-600">Warning counts come from the bounded support sample. Full configuration opens with the tenant.</p>
            <ul className="mt-3 divide-y rounded border border-gray-200 bg-white">
              {tenants.map((row) => {
                const view = supportByTenant[row.id];
                const count = view ? (view.warnings || []).length : null;
                return (
                  <li key={row.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                    <span>{row.name} <span className="text-xs text-gray-500">{row.id}</span> {row.statusLabel === 'Deleted' ? <em>Deleted</em> : null}</span>
                    <span>{count == null ? 'Unavailable' : `${count} warning(s)`}</span>
                    <button type="button" className="text-blue-700 underline" onClick={() => updateLocation({ tenantId: row.id, tab: 'configuration' })}>Open configuration</button>
                  </li>
                );
              })}
            </ul>
          </section>
        ) : null}

        {selected && !tabDenied ? (
          <div className="mt-4" data-testid="selected-tenant-workspace">
            {tenants.find((row) => row.id === location.tenantId)?.statusLabel === 'Deleted' ? (
              <p className="mb-2 text-sm text-gray-600" data-testid="deleted-tenant-banner">This tenant is deleted. This view is read-only.</p>
            ) : null}
            <TenantDetailPage
              key={`${location.tenantId}-${refreshTick}`}
              embedded
              tenantId={location.tenantId}
              tab={activeTab}
              month={month}
              onTabChange={(tab) => updateLocation({ tab })}
              onMonthChange={(value) => updateLocation({ month: value })}
            />
          </div>
        ) : null}
      </Box>
    </MainLayout>
  );
}

function AllTenantTable({
  tenants,
  billingById,
  billingMeasured,
  supportByTenant,
  supportCoverage,
  showSupport = true,
  showBilling = true,
  onOpen,
}) {
  const headings = ['Tenant', 'Status'];
  if (showSupport) headings.push('Warnings', 'Agents', 'Voice');
  if (showBilling) headings.push('Vendor cost', 'Pending');
  headings.push('Data usage', '');
  return (
    <section className="mt-4" data-testid="all-tenants-overview">
      <p className="text-sm text-gray-600">
        {showBilling ? 'Billing figures are the existing Twilio vendor report for the selected month. ' : ''}
        {showSupport
          ? `Support evidence is ${supportCoverage === 'partial' ? `partial (first ${ALL_TENANT_SUPPORT_CAP} tenants)` : 'from the current tenant list'}. `
          : ''}
        Data usage is unavailable until a tenant is opened.
      </p>
      <div className="mt-3 overflow-x-auto rounded border border-gray-200 bg-white">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              {headings.map((heading) => (
                <th key={heading || 'action'} className="px-3 py-2 font-medium">{heading}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tenants.map((row) => {
              const bill = billingById.get(row.id);
              const support = supportByTenant[row.id];
              const agent = (support?.cards || []).find((card) => card.id === 'agents');
              const voice = (support?.cards || []).find((card) => card.id === 'voice');
              return (
                <tr key={row.id} className="border-t border-gray-100" data-testid={`overview-row-${row.id}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium">{row.name}</div>
                    <div className="text-xs text-gray-500">{row.id}</div>
                  </td>
                  <td className="px-3 py-2">{row.statusLabel}</td>
                  {showSupport ? (
                    <>
                      <td className="px-3 py-2">{support ? `${(support.warnings || []).length}` : 'Unavailable'}</td>
                      <td className="px-3 py-2">{agent ? `${agent.status} (${agent.measurement})` : 'Unavailable'}</td>
                      <td className="px-3 py-2">{voice ? `${voice.status} (${voice.measurement})` : 'Unavailable'}</td>
                    </>
                  ) : null}
                  {showBilling ? (
                    <>
                      <td className="px-3 py-2">{billingMeasured ? (bill ? formatVendorCosts(bill.vendorCostByCurrency) : 'No Twilio usage this month') : 'Unavailable'}</td>
                      <td className="px-3 py-2">{billingMeasured ? (bill?.pendingCount ?? 0) : '—'}</td>
                    </>
                  ) : null}
                  <td className="px-3 py-2">Unavailable</td>
                  <td className="px-3 py-2">
                    <button type="button" className="text-blue-700 underline" onClick={() => onOpen(row.id, 'overview')}>Open tenant</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function AllTenantSupport({ tenants, supportByTenant, supportCoverage, onOpen }) {
  const rows = [];
  tenants.forEach((tenant) => {
    const view = supportByTenant[tenant.id];
    (view?.warnings || []).forEach((warning) => {
      rows.push({ tenant, warning });
    });
  });
  return (
    <section className="mt-4" data-testid="all-tenants-support">
      <p className="text-sm text-gray-600">
        {supportCoverage === 'partial'
          ? `Warnings from the first ${ALL_TENANT_SUPPORT_CAP} tenants. This is not a complete cross-tenant scan.`
          : 'Warnings from the loaded tenant list. Each row opens that tenant’s Support tab.'}
      </p>
      {rows.length === 0 ? <p className="mt-3 text-sm text-gray-500">No warnings in the loaded sample.</p> : (
        <ul className="mt-3 divide-y rounded border border-gray-200 bg-white">
          {rows.map((row) => (
            <li key={`${row.tenant.id}-${row.warning.id}`} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
              <span>{row.tenant.name}: {row.warning.message}</span>
              <button type="button" className="text-blue-700 underline" onClick={() => onOpen(row.tenant.id)}>Open support</button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function TenantActionList({ tenants, actionLabel, onOpen }) {
  return (
    <ul className="mt-3 divide-y rounded border border-gray-200 bg-white">
      {tenants.map((row) => (
        <li key={row.id} className="flex items-center justify-between px-3 py-2 text-sm">
          <span>{row.name} {row.statusLabel === 'Deleted' ? <em>Deleted</em> : null} <span className="text-xs text-gray-500">{row.id}</span></span>
          <button type="button" className="text-blue-700 underline" onClick={() => onOpen(row.id)}>{actionLabel}</button>
        </li>
      ))}
    </ul>
  );
}
